import { create } from 'zustand';
import type { AgentEvent, ApprovalRequest } from '../types';

export type SkillStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped';

export interface SkillCardData {
  name: string;
  status: SkillStatus;
  startTime?: number;
  elapsed?: number;
  summary?: string;
  metrics?: Record<string, unknown>;
}

export interface SkillProgress {
  processed: number;
  total: number;
  detail?: string;
}

interface SSEState {
  connected: boolean;
  // Agent feed events
  agentEvents: AgentEvent[];
  // Skill statuses for sidebar
  skillStatuses: Record<string, SkillCardData>;
  // Live skill progress (processed / total)
  skillProgress: Record<string, SkillProgress>;
  // Agent turn count
  agentTurnCount: number;
  // Pending approval (skill approval or result review)
  pendingApproval: ApprovalRequest | null;

  // Actions
  setConnected: (c: boolean) => void;
  addAgentEvent: (e: AgentEvent) => void;
  clearAgentEvents: () => void;
  setSkillStatus: (name: string, data: Partial<SkillCardData>) => void;
  setSkillProgress: (skill: string, progress: SkillProgress) => void;
  clearSkillProgress: (skill: string) => void;
  setAgentTurnCount: (n: number) => void;
  setPendingApproval: (req: ApprovalRequest | null) => void;
  resetForNewRun: () => void;
}

export const useSSEStore = create<SSEState>((set) => ({
  connected: false,
  agentEvents: [],
  skillStatuses: {},
  skillProgress: {},
  agentTurnCount: 0,
  pendingApproval: null,

  setConnected: (connected) => set({ connected }),
  addAgentEvent: (e) => set((s) => ({ agentEvents: [...s.agentEvents, e] })),
  clearAgentEvents: () => set({ agentEvents: [] }),

  setSkillStatus: (name, data) =>
    set((s) => ({
      skillStatuses: {
        ...s.skillStatuses,
        [name]: { ...({ name, status: 'pending' } as SkillCardData), ...s.skillStatuses[name], ...data },
      },
    })),

  setSkillProgress: (skill, progress) =>
    set((s) => ({
      skillProgress: { ...s.skillProgress, [skill]: progress },
    })),

  clearSkillProgress: (skill) =>
    set((s) => {
      const next = { ...s.skillProgress };
      delete next[skill];
      return { skillProgress: next };
    }),

  setAgentTurnCount: (agentTurnCount) => set({ agentTurnCount }),
  setPendingApproval: (pendingApproval) => set({ pendingApproval }),
  resetForNewRun: () =>
    set({
      agentEvents: [],
      skillStatuses: {},
      skillProgress: {},
      agentTurnCount: 0,
      pendingApproval: null,
    }),
}));

/**
 * True while diagram extraction is running by EITHER path:
 *  - the automatic post-research run → `skillStatuses.diagram_extraction` is
 *    `running` (fed from progress, cleared on completion), or
 *  - the manual "Extract All" button → `skillProgress.extract_all` is active.
 * One flag drives the shared progress panel + the Extract All button lock.
 */
export function useIsExtractingDiagrams(): boolean {
  return useSSEStore((state) => {
    const extractAllProgress = state.skillProgress['extract_all'];
    const extractAllActive =
      !!extractAllProgress &&
      extractAllProgress.total > 0 &&
      extractAllProgress.processed < extractAllProgress.total;
    const autoRunActive = state.skillStatuses['diagram_extraction']?.status === 'running';
    return extractAllActive || autoRunActive;
  });
}
